/** Viewport-related shared state and the imperative viewport API. */
import { computed, effect, signal } from '@preact/signals';
import { autoLevels, marchingSquares, type ContourLevel } from '../core/contours';
import type { Vec2 } from '../core/types';
import { display, measurement, tool, windowRange } from '../app/store';
import type { View } from './transform';

export const viewSig = signal<View>({ scale: 1, tx: 0, ty: 0 });
/** Snap ROI points to pixel centres (Ctrl/Cmd inverts while dragging). */
export const snapPixels = signal(false);
/** Live capture video shown in the viewport instead of the frozen image. */
export const liveVideo = signal<HTMLVideoElement | null>(null);
/** Pending spatial-calibration line length (px), set by the calibrate tool. */
export const calibrationRequest = signal<{ lengthPx: number } | null>(null);
export const polygonDraft = signal<{ pts: Vec2[]; cursor: Vec2 | null } | null>(null);

// Switching away from the polygon tool (by key or toolbar) cancels an unfinished polygon.
effect(() => {
  if (tool.value !== 'polygon' && polygonDraft.peek()) polygonDraft.value = null;
});

export interface ViewportApi {
  fit(): void;
  oneToOne(): void;
  zoomBy(f: number): void;
  centerOn(x: number, y: number): void;
  /** PNG of the viewport as currently displayed (image + overlay). */
  snapshotView(): Promise<Blob | null>;
}

export const viewportApi: { current: ViewportApi | null } = { current: null };

/** Contour lines of the measurement plane (computed only when enabled). */
export const contourData = computed<ContourLevel[] | null>(() => {
  const d = display.value;
  // Contours are not recomputed for every live frame; they return when the frame is frozen.
  if (!d.contours.enabled || liveVideo.value) return null;
  const m = measurement.value;
  if (!m) return null;
  const [lo, hi] = windowRange.value;
  const levels = d.contours.auto ? autoLevels(lo, hi, d.contours.count, d.contours.logSpacing) : d.contours.levels.filter(Number.isFinite);
  if (!levels.length) return [];
  const n = m.plane.width * m.plane.height;
  const stride = Math.max(1, Math.ceil(Math.sqrt(n / 4e6)));
  return marchingSquares(m.plane, levels, stride);
});
